import AbstractGenerator from '@scene/misc/generator';
import StraightConnection from './objects/straight-connection';
import CornerConnection from './objects/corner-connection';
import LShapedConnection from './objects/l-shaped-connection';
import * as THREE from 'three';
import NailPlate from '@scene/objects/nail/nail-plate';
import baseOptions from './defaultOptions';
import Line from '@scene/objects/line';
import Arrow from '@scene/objects/arrow';
import { HTMLEditableLabel } from '@scene/objects/html-label';
import { unitTransform } from '@services/helper.service';
import Scene from '@scene';
import { defaultUnits } from '@config';
import { createArrow } from '@scene/services/helper';

export default class WoodConnectionObject extends AbstractGenerator {

  static VERTICAL_BEAM_HEIGHT_OFFSET = 25;
  static types = {
    STRAIGHT: 0,
    L_SHAPED: 1,
    CORNER: 2
  };

  static rotationConfig = {
    [WoodConnectionObject.types.STRAIGHT]: {
      '2D': new THREE.Euler(-Math.PI / 2, 0, 0),
    },
    [WoodConnectionObject.types.CORNER]: {
      '2D': new THREE.Euler(-Math.PI / 2, -Math.PI / 2, 0),
    },
    [WoodConnectionObject.types.L_SHAPED]: {
      '2D': new THREE.Euler(-Math.PI / 2, 0, 0),
    }
  };

  constructor(options = {}, defaultOptions = baseOptions) {
    super(options, defaultOptions);
    this.params = {
      material: options.material || 'S235',
    };
  }

  createConnection() {
    let connection;

    switch (this.options.type) {
      case WoodConnectionObject.types.STRAIGHT:
        connection = new StraightConnection(this.options, this.dispatchOptionUpdate.bind(this));
        break;
      case WoodConnectionObject.types.L_SHAPED:
        connection = new LShapedConnection(this.options, this.dispatchOptionUpdate.bind(this));
        break;
      case WoodConnectionObject.types.CORNER:
        connection = new CornerConnection(this.options, this.dispatchOptionUpdate.bind(this));
        break;
      default:
        throw new Error('Unknown connection type!');
    }

    return connection;
  }

  setOrientation() {
  }

  init2DObject() {
    this.options.mode = '2D';

    const object = this.createConnection();
    object.init();

    this.layer2D.set(object.layer2D);
  }

  init3DObject() {
    this.options.mode = '3D';

    const object = this.createConnection();
    object.init();

    this.layer3D.set(object.layer3D);
  }

  get mesh() { return this.object.mesh; }
  get htmlLayer() { return this.object.htmlLayer; }

  dispose() {
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          } else {
            if (child.material) child.material.dispose();
          }
        }
      });
    }
  }

  getMesh() {
    return this.connection.mesh;
  }

  render(camera, renderer) {
    super.render(camera, renderer);

    // We are updating label positions for all NailGroups
    if (this.connection && this.connection.mesh) {
      const nailPlates = this.connection.mesh.children.filter(child => child instanceof NailPlate);
      nailPlates.forEach(plate => {
        if (plate._nailGroup) {
          plate._nailGroup.updateLabels(camera, renderer);
        }
        if (plate._nailGroupB) {
          plate._nailGroupB.updateLabels(camera, renderer);
        }
      });
    }
  }

  static createLoads(initialPos, faxd, dispatchOptionUpdate) {
    const RED = 0xff0000;
    const RED_HTML = "#ff0000";
    
    const arrowYoffset = 50;

    let dashedLine = new Line({
      from: initialPos,
      to: new THREE.Vector3(initialPos.x, initialPos.y + arrowYoffset, initialPos.z),
      disableDepthTest: true,
      dashed: true,
      customMaterial: new THREE.LineDashedMaterial({
        color: RED,
        linewidth: 1,
        scale: 1,
        dashSize: 6,
        gapSize: 5
      })
    });

    const arrowSize = 50;
    const arrow = createArrow(Arrow.types.direction.NegativeY, true, {
      size: arrowSize,
      color: RED,
      position: new THREE.Vector3(initialPos.x, initialPos.y + arrowYoffset, initialPos.z)
    });

    const faxDValue = unitTransform(+faxd).from(defaultUnits.LOAD).to(Scene.units.load);
    const labelObject = new HTMLEditableLabel({
      text: `Fax.d: ${faxDValue}`,
      value: faxDValue,
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      color: RED_HTML,
      bold: true
    });
    
    labelObject.onInputChange((val, didChange) => {
      dispatchOptionUpdate({
        panel: {
          loads: {
            faxd: unitTransform(+val, false).from(Scene.units.load).to(defaultUnits.LOAD) 
          }
        }
      },
      false,
      didChange);
    });
    
    labelObject.position.set(
      initialPos.x,
      initialPos.y + arrowYoffset + arrowSize,
      initialPos.z
    );

    return {
      dashedLine,
      arrow,
      label: labelObject
    };
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/wood-connection/wood-connection.object.js